// src/pages/scan/ScanPage.tsx
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import {
  getTotalScanCount,
  startScanV2,
  fetchScanProgress,
} from '../../api/scanService';

import ScanResults       from '../../components/scan/ScanResults';
import ScanForm          from '../../components/scan/ScanForm/ScanForm';
import ReportModal       from '../../components/scan/ReportModal';
import ParticlesScan     from '../../components/particles/ParticlesScan';
import ScannerFeatures   from '../../components/scan/ScannerFeatures';
import Footer            from '../../components/LandingPage/Footer/Footer';
import ConfirmationModal from '../../components/scan/ConfirmationModal';
import Notification      from '../../components/misc/Notification';

import {
  ScanReportRequest,
  ScanReportResponse,
  InitScanPayload,
  BlurredLeak,
} from '../../types/interfaces';
import { submitScanReportRequest } from '../../api/contactFormService';
import Navbar from '../../components/LandingPage/Navbar/Navbar';
import ScanHeader from '../../components/scan/Header/ScanHeader';

interface ProgressSections {
  emails: number;
  leaks: number;
  ips: number;
  impersonation: number;
  vulnerabilities: number;
  similar_companies: number;
  global: number;
}

const ScanPage: React.FC = () => {
  const { t, i18n } = useTranslation();

  /* ───────── estados básicos ───────── */
  const [domain, setDomain]       = useState('');
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState<string | null>(null);
  const [result, setResult]       = useState<InitScanPayload | null>(null);
  const [scanCount, setScanCount] = useState(0);
  const [scanId, setScanId]       = useState<string | null>(null);

  /* ───────── progreso vs contadores ───────── */
  const initProgress: ProgressSections = {
    emails: 0, leaks: 0, ips: 0,
    impersonation: 0, vulnerabilities: 0, global: 0, similar_companies: 0
  };
  const [progress, setProgress]           = useState(initProgress);
  const [securityScore, setSecurityScore] = useState(100);

  // contadores numéricos
  const [emailsCount, setEmailsCount]             = useState(0);
  const [ipsCount, setIpsCount]                   = useState(0);
  const [impersonationCount, setImpersonationCount] = useState(0);
  const [leaksCount, setLeaksCount]               = useState(0);
  const [vulnerabilitiesCount, setVulnerabilitiesCount] = useState(0);

  // datos “blurreados”
  const [maskedEmails, setMaskedEmails] = useState<string[]>([]);
  const [blurredLeaks, setBlurredLeaks] = useState<BlurredLeak[]>([]);

  /* ───────── similar companies ───────── */
  const [similarCompanies, setSimilarCompanies] = useState<{
    organizationFound: boolean;
    totalSimilar: number;
    sameCity: number;
    sameCountry: number;
  } | null>(null);

  /* ───────── report / UI ───────── */
  const [reportEmail,      setReportEmail]      = useState('');
  const [privacyAccepted,  setPrivacyAccepted]  = useState(false);
  const [showReportModal,  setShowReportModal]  = useState(false);
  const [showCancelModal,  setShowCancelModal]  = useState(false);
  const [showNotification, setShowNotification] = useState(false);
  const [notificationMsg,  setNotificationMsg]  = useState('');

  /* ───────── scan history ───────── */
  const [scanHistory, setScanHistory] = useState<{
    domain?: string | null;
    lastScanDate?: string | null;
    scansLastYear?: number;
  } | null>(null);

  /* ───────── stats globales ───────── */
  useEffect(() => {
    getTotalScanCount().then(setScanCount);
  }, []);

  /* ───────── open report modal on complete ───────── */
  useEffect(() => {
    if (result?.status === 'completed') {
      setShowReportModal(true);
    }
  }, [result]);

  /* ───────── polling de progreso cada 2s ───────── */
  useEffect(() => {
    if (!scanId) return;
    let isCanceled = false;
    let startTime = Date.now();
  
    const poll = async () => {
      if (isCanceled) return;
  
      try {
        const p = await fetchScanProgress(scanId);
  
        // 1) actualizar barras
        setProgress({
          emails:           p.progress_emails,
          leaks:            p.progress_leaks,
          ips:              p.progress_ips,
          impersonation:    p.progress_spoofing,
          vulnerabilities:  p.progress_vulnerabilities,
          similar_companies:p.progress_similar_companies,
          global:           p.progress_general,
        });
  
        // 2) actualizar contadores y datos 
        setSecurityScore(p.security_score);
        setEmailsCount(p.count_emails);
        setIpsCount(p.count_ips);
        setImpersonationCount(p.count_spoofing);
        setLeaksCount(p.count_leaks);
        setVulnerabilitiesCount(p.count_vulnerabilities);
  
        setMaskedEmails(p.blurred_emails.map((b: any) => b.email_blurred));
        setBlurredLeaks(
          p.blurred_leaks.map((l: any) => ({
            database_name:    l.database_name,
            email:            l.email_blurred,

            /* credenciales */
            password:         l.password_blurred,
            hashed_password:  l.hashed_password_blurred,
            username:         l.username_blurred,

            /* datos personales */
            name:             l.name_blurred,
            first_name:       l.first_name_blurred,
            last_name:        l.last_name_blurred,
            dob:              l.dob_blurred,
            gender:           l.gender_blurred,

            /* contacto / ubicación */
            phone:            l.phone_blurred,
            ip_address:       l.ip_address_blurred,
            address:          l.address_blurred,
            city:             l.city_blurred,
            state:            l.state_blurred,
            province:         l.province_blurred,
            zip:              l.zip_blurred,
            country:          l.country_blurred,

            /* metadatos */
            origin:           l.origin_blurred,
            source_name:      l.source_name,
            source_breach_date: l.source_breach_date,
          }))
        );
  
        setSimilarCompanies({
          organizationFound: p.similar_companies.total > 0,
          totalSimilar:      p.similar_companies.total,
          sameCity:          p.similar_companies.same_city,
          sameCountry:       p.similar_companies.same_country,
        });
  
        // 3) si ya terminó, paramos y dejamos de cargar
        if (p.status === 'completed') {
          setResult(r => r && ({ ...r, status: 'completed' }));
          setLoading(false);
          return;
        }
  
        // 4) programar siguiente poll
        const elapsed = (Date.now() - startTime) / 1000; // segundos
        const nextInterval = elapsed < 10 ? 2000 : 5000;
        setTimeout(poll, nextInterval);
  
      } catch (err) {
        console.error('Progress polling error', err);
        // en caso de error reintentamos en 5s
        setTimeout(poll, 5000);
      }
    };
  
    // arranco el polling de inmediato
    poll();
  
    return () => {
      isCanceled = true;
    };
  }, [scanId]);

  /* ───────── reset completo ───────── */
  const resetScanState = () => {
    setResult(null);
    setProgress(initProgress);
    setSecurityScore(100);
    setEmailsCount(0);
    setIpsCount(0);
    setImpersonationCount(0);
    setLeaksCount(0);
    setVulnerabilitiesCount(0);
    setMaskedEmails([]);
    setBlurredLeaks([]);
    setSimilarCompanies(null);
    setError(null);
    setScanId(null);
  };

  /* ───────── arrancar nuevo scan ───────── */
  const analyzeSecurity = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!domain.trim()) {
      setError(t('scan.notFound'));
      return;
    }
    resetScanState();
    setLoading(true);

    try {
      const init = await startScanV2(domain);
      setResult(init);
      setSecurityScore(init.securityScore);
      setScanHistory({
        domain:       init.domain,
        lastScanDate: init.lastScanDate,
        scansLastYear: init.scansLastYear,
      });
      setScanId(init.scan_id);

    } catch (err: any) {
      const api = err?.response?.data?.detail;
      const code = api?.code as string | undefined;

      const uiMsg = code
        ? t(`scan.errors.${code}`)
        : api?.message ?? err.message ?? t('scan.genericError');

      setError(uiMsg);
      setLoading(false);
    }
  };

  /* ───────── cancelar en curso ───────── */
  const cancelScan = () => {
    resetScanState();
    setShowCancelModal(false);
    setLoading(false);
    setNotificationMsg(t('scan.cancelled'));
    setShowNotification(true);
  };

  /* ───────── envío reporte ───────── */
  const handleReportSubmit = async () => {
    const data: ScanReportRequest = {
      domain, email: reportEmail, language_code: i18n.language || 'es',
    };
    const r: ScanReportResponse = await submitScanReportRequest(data);
    setNotificationMsg(r.error ?? t('scan.reportModal.notificationSent'));
    setShowNotification(true);
    setShowReportModal(false);
    setReportEmail('');
    setPrivacyAccepted(false);
  };

  /* ─────────────── render ─────────────── */
  return (
    <div className="min-h-screen flex flex-col relative overflow-hidden bg-background">
      {/* fondo y partículas */}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-br from-secondary to-primary" />
        <ParticlesScan />
      </div>

      <Navbar fixedTop={0} hideScan />

      <div className="relative z-10 flex-grow pt-48">
        <div className="container mx-auto px-4 w-full">
          <ScanHeader domain={domain} loading={loading} />

          {/* formulario */}
          <ScanForm
            domain={domain}
            setDomain={setDomain}
            onSubmit={analyzeSecurity}
            loading={loading}
            error={error}
            target={scanCount}
            onCancel={() => setShowCancelModal(true)}
          />

          {/* resultados vs features */}
          {result ? (
            <ScanResults
              result={{ ...result, securityScore }}
              progress={progress}
              emailsCount={emailsCount}
              ips={ipsCount}
              impersonationCount={impersonationCount}
              leaksCount={leaksCount}
              vulnerabilitiesCount={vulnerabilitiesCount}
              maskedEmails={maskedEmails}
              blurredLeaks={blurredLeaks}
              domain={domain}
              t={t}
              onOpenReport={() => setShowReportModal(true)}
              similarCompanies={similarCompanies ?? undefined}
              resetTable={loading}
              scanHistory={scanHistory}
            />
          ) : (
            <ScannerFeatures />
          )}
        </div>
      </div>

      {/* modales */}
      {showReportModal && (
        <ReportModal
          domain={domain}
          reportEmail={reportEmail}
          setReportEmail={setReportEmail}
          privacyAccepted={privacyAccepted}
          setPrivacyAccepted={setPrivacyAccepted}
          onClose={() => setShowReportModal(false)}
          onSubmit={handleReportSubmit}
          t={t}
        />
      )}

      <Footer />

      {showCancelModal && (
        <ConfirmationModal
          message={t('scan.cancelConfirm')}
          onConfirm={cancelScan}
          onCancel={() => setShowCancelModal(false)}
          t={t}
        />
      )}

      {showNotification && (
        <Notification
          message={notificationMsg}
          onClose={() => setShowNotification(false)}
        />
      )}
    </div>
  );
};

export default ScanPage;
